import { ListChecks, RefreshCw, Play, Pause, Trash2, AlertCircle } from 'lucide-react'
import { useQueueStatus, usePauseQueue, useResumeQueue, useCleanQueue, useRetryFailedJobs } from '@hooks/useQueues'

export function QueuePage() {
  const { data: queueStatus, isLoading, error, refetch } = useQueueStatus(5000)
  const pauseMutation = usePauseQueue()
  const resumeMutation = useResumeQueue()
  const cleanMutation = useCleanQueue()
  const retryMutation = useRetryFailedJobs()

  // Calculate totals across all queues
  const totals = queueStatus?.queues.reduce(
    (acc, queue) => ({
      active: acc.active + queue.active,
      waiting: acc.waiting + queue.waiting,
      failed: acc.failed + queue.failed,
      completed: acc.completed + queue.completed,
      delayed: acc.delayed + queue.delayed,
    }),
    { active: 0, waiting: 0, failed: 0, completed: 0, delayed: 0 }
  )

  const handlePauseQueue = (queueName: string) => {
    pauseMutation.mutate(queueName)
  }

  const handleResumeQueue = (queueName: string) => {
    resumeMutation.mutate(queueName)
  }

  const handleCleanQueue = (queueName: string, status: 'completed' | 'failed') => {
    if (window.confirm(`Clean all ${status} jobs from ${queueName}?`)) {
      cleanMutation.mutate({ queueName, status, age: 24 })
    }
  }

  const handleRetryFailed = (queueName: string) => {
    if (window.confirm(`Retry all failed jobs in ${queueName}?`)) {
      retryMutation.mutate({ queueName, limit: 100 })
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <ListChecks size={32} className="text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">Queue Monitor</h1>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle size={20} />
            <p className="font-medium">Failed to load queue status</p>
          </div>
          <p className="mt-2 text-sm text-red-600">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ListChecks size={32} className="text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">Queue Monitor</h1>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-medium text-gray-600">Active Jobs</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {isLoading ? '...' : totals?.active || 0}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-medium text-gray-600">Waiting</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">
            {isLoading ? '...' : totals?.waiting || 0}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-medium text-gray-600">Delayed</h3>
          <p className="mt-2 text-3xl font-bold text-yellow-600">
            {isLoading ? '...' : totals?.delayed || 0}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-medium text-gray-600">Failed</h3>
          <p className="mt-2 text-3xl font-bold text-red-600">
            {isLoading ? '...' : totals?.failed || 0}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-medium text-gray-600">Completed</h3>
          <p className="mt-2 text-3xl font-bold text-gray-600">
            {isLoading ? '...' : totals?.completed || 0}
          </p>
        </div>
      </div>

      {/* Individual Queue Details */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Queue Details</h2>
          <p className="mt-1 text-sm text-gray-600">
            Real-time monitoring of BullMQ job queues (auto-refreshes every 5 seconds)
          </p>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading queue data...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {queueStatus?.queues.map((queue) => (
                <div
                  key={queue.name}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{queue.name}</h3>
                      {queue.paused && (
                        <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                          Paused
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {queue.paused ? (
                        <button
                          onClick={() => handleResumeQueue(queue.name)}
                          disabled={resumeMutation.isPending}
                          className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          <Play size={14} />
                          Resume
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePauseQueue(queue.name)}
                          disabled={pauseMutation.isPending}
                          className="flex items-center gap-1 rounded-lg bg-yellow-600 px-3 py-1 text-sm font-medium text-white hover:bg-yellow-700 disabled:opacity-50"
                        >
                          <Pause size={14} />
                          Pause
                        </button>
                      )}
                      {queue.failed > 0 && (
                        <button
                          onClick={() => handleRetryFailed(queue.name)}
                          disabled={retryMutation.isPending}
                          className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          <RefreshCw size={14} />
                          Retry Failed
                        </button>
                      )}
                      {queue.completed > 100 && (
                        <button
                          onClick={() => handleCleanQueue(queue.name, 'completed')}
                          disabled={cleanMutation.isPending}
                          className="flex items-center gap-1 rounded-lg bg-gray-600 px-3 py-1 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
                        >
                          <Trash2 size={14} />
                          Clean
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-6">
                    <div>
                      <p className="text-xs text-gray-600">Active</p>
                      <p className="mt-1 text-lg font-semibold text-green-600">{queue.active}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Waiting</p>
                      <p className="mt-1 text-lg font-semibold text-blue-600">{queue.waiting}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Delayed</p>
                      <p className="mt-1 text-lg font-semibold text-yellow-600">{queue.delayed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Failed</p>
                      <p className="mt-1 text-lg font-semibold text-red-600">{queue.failed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Completed</p>
                      <p className="mt-1 text-lg font-semibold text-gray-600">{queue.completed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Total</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">{queue.total}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Last Updated */}
      {queueStatus?.timestamp && (
        <p className="text-center text-xs text-gray-500">
          Last updated: {new Date(queueStatus.timestamp).toLocaleTimeString()}
        </p>
      )}
    </div>
  )
}
